import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

import {
  ArtikelkatalogEintrag,
  WareneingangDetail,
  WareneingangKpis,
} from '../../models/wareneingang.model';
import { WareneingangPosition } from '../../models/wareneingang-position.model';
import { WareneingangStatus } from '../../models/wareneingang-status.enum';
import { WareneingaengeService } from '../../services/wareneingaenge.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  Subscription,
  finalize,
  timer,
} from 'rxjs';

interface WarengruppeBlock {
  warengruppe: string;
  positionen:
  WareneingangPosition[];
}

interface ArtikelAddFeedback {
  artikelname: string;
}

type DetailSortColumn =
  'artikel';

type DetailSortDirection =
  'asc' | 'desc';

interface DetailSortState {
  column:
  DetailSortColumn |
  null;

  direction:
  DetailSortDirection;
}

@Component({
  selector:
    'app-wareneingang-detail',

  standalone:
    true,

  imports: [
    CommonModule,
    MatIconModule,
  ],

  templateUrl:
    './wareneingang-detail.component.html',

  styleUrl:
    './wareneingang-detail.component.scss',

  changeDetection:
    ChangeDetectionStrategy.OnPush,
})
export class WareneingangDetailComponent
  implements OnDestroy {

  private readonly route =
    inject(ActivatedRoute);

  private readonly router =
    inject(Router);

  private readonly wareneingaengeService =
    inject(
      WareneingaengeService
    );

  private readonly authService =
    inject(AuthService);

  private readonly toastService =
    inject(ToastService);

  private readonly currentTime =
    signal(
      Date.now()
    );

  protected readonly WareneingangStatus =
    WareneingangStatus;

  protected readonly currentUserName =
    this.authService
      .getLoggedInUserName();

  protected readonly isPosSession =
    this.authService
      .isPosSession();

  protected readonly currentId =
    signal<
      string |
      null
    >(null);

  protected readonly wareneingang =
    signal<
      WareneingangDetail |
      null
    >(null);

  protected readonly draftPositionen =
    signal<
      WareneingangPosition[]
    >([]);

  protected readonly isEditMode =
    signal(false);

  protected readonly searchTerm =
    signal('');

  protected readonly isAddArtikelDialogOpen =
    signal(false);

  protected readonly artikelSucheTerm =
    signal('');

  protected readonly artikelAddFeedback =
    signal<
      ArtikelAddFeedback |
      null
    >(null);

  protected readonly detailSortState =
    signal<DetailSortState>({
      column:
        null,

      direction:
        'asc',
    });

  protected readonly isLoading =
    signal(true);

  protected readonly verfuegbareArtikel =
    signal<
      ArtikelkatalogEintrag[]
    >([]);

  protected readonly skeletonKpiCards =
    Array.from({
      length: 4,
    });

  protected readonly skeletonRows =
    Array.from({
      length: 6,
    });

  private artikelAddFeedbackTimeoutId:
    ReturnType<
      typeof setTimeout
    > |
    null = null;

  private isAbschlussNichtMoeglichToastVisible =
    false;

  private abschlussNichtMoeglichToastTimeoutId:
    ReturnType<
      typeof setTimeout
    > |
    null = null;

  private isBearbeitungsmodusToastVisible =
    false;

  private bearbeitungsmodusToastTimeoutId:
    ReturnType<
      typeof setTimeout
    > |
    null = null;

  private deadlineTimerSubscription?:
    Subscription;

  private lockExpiryTimerId:
    number | null = null;

  private changeHeartbeatTimerId:
    number | null = null;

  private readonly LOCK_EXPIRY_SAFETY_MS =
    1_500;

  private readonly CHANGE_HEARTBEAT_DEBOUNCE_MS =
    300;

  protected readonly currentPositionen =
    computed(() => {
      if (
        this.isEditMode()
      ) {
        return this.draftPositionen();
      }

      return (
        this.wareneingang()
          ?.positionen ??
        []
      );
    });

  protected readonly displayedPositionen =
    computed(() => {
      const term =
        this.searchTerm()
          .trim()
          .toLowerCase();

      if (!term) {
        return this.currentPositionen();
      }

      return this.currentPositionen()
        .filter(
          (position) => {
            return (
              position.artikelnummer
                .toLowerCase()
                .includes(
                  term
                ) ||
              position.artikelname
                .toLowerCase()
                .includes(
                  term
                ) ||
              position.warengruppe
                .toLowerCase()
                .includes(
                  term
                )
            );
          }
        );
    });

  protected readonly groupedPositionen =
    computed<
      WarengruppeBlock[]
    >(() => {
      const groups =
        new Map<
          string,
          WareneingangPosition[]
        >();

      for (
        const position of
        this.displayedPositionen()
      ) {
        const warengruppe =
          position.warengruppe ||
          'Ohne Warengruppe';

        const currentGroup =
          groups.get(
            warengruppe
          ) ??
          [];

        currentGroup.push(
          position
        );

        groups.set(
          warengruppe,
          currentGroup
        );
      }

      const groupedPositionen =
        Array.from(
          groups.entries()
        ).map(
          ([
            warengruppe,
            positionen,
          ]) => ({
            warengruppe,
            positionen,
          })
        );

      return this.sortWarengruppen(
        groupedPositionen
      );
    });

  protected readonly kpis =
    computed<
      WareneingangKpis
    >(() => {
      const positionen =
        this.currentPositionen();

      return {
        positionen:
          positionen.length,

        liefermengeGesamt:
          positionen.reduce(
            (
              sum,
              position
            ) =>
              sum +
              position.liefermenge,

            0
          ),

        istMengeGesamt:
          positionen.reduce(
            (
              sum,
              position
            ) =>
              sum +
              (
                position.istMenge ??
                0
              ),

            0
          ),

        abweichungen:
          this.wareneingang()
            ?.anzahlKorrekturen ??
          0,
      };
    });

  protected readonly isLockedByAnotherUser =
    computed(() => {
      const wareneingang =
        this.wareneingang();

      return !!(
        wareneingang
          ?.istGesperrt &&
        wareneingang
          .gesperrtVon &&
        wareneingang
          .gesperrtVon !==
        this.currentUserName
      );
    });

  protected readonly isOverdue =
    computed(() => {
      const wareneingang =
        this.wareneingang();

      if (
        !wareneingang ||
        wareneingang.status ===
        WareneingangStatus.Abgeschlossen
      ) {
        return false;
      }

      const deadline =
        this.getDeadlineTimestamp(
          wareneingang
        );

      return (
        deadline !==
        null &&
        this.currentTime() >=
        deadline
      );
    });

  protected readonly canStartEdit =
    computed(() => {
      const wareneingang =
        this.wareneingang();

      return !!(
        wareneingang &&
        wareneingang.status !==
        WareneingangStatus.Abgeschlossen &&
        !this.isOverdue() &&
        !this.isLockedByAnotherUser()
      );
    });

  protected readonly editDisabledHint =
    computed(() => {
      const wareneingang =
        this.wareneingang();

      if (!wareneingang) {
        return '';
      }

      if (
        wareneingang.status ===
        WareneingangStatus.Abgeschlossen
      ) {
        return 'Dieser Wareneingang ist abgeschlossen und kann nicht mehr bearbeitet werden.';
      }

      if (
        this.isOverdue()
      ) {
        return 'Dieser Wareneingang ist überfällig und kann nicht mehr bearbeitet werden.';
      }

      if (
        this.isLockedByAnotherUser()
      ) {
        return wareneingang
          .gesperrtVon
          ? `Dieser Wareneingang wird aktuell von ${wareneingang.gesperrtVon} bearbeitet.`
          : 'Dieser Wareneingang wird aktuell von einem anderen Benutzer bearbeitet.';
      }

      return '';
    });

  protected readonly artikelSuchergebnisse =
    computed<
      ArtikelkatalogEintrag[]
    >(() => {
      const term =
        this.artikelSucheTerm()
          .trim()
          .toLowerCase();

      if (!term) {
        return this.verfuegbareArtikel();
      }

      return this.verfuegbareArtikel()
        .filter(
          (artikel) => {
            return (
              artikel.artikelnummer
                .toLowerCase()
                .includes(
                  term
                ) ||
              artikel.artikelname
                .toLowerCase()
                .includes(
                  term
                )
            );
          }
        );
    });

  constructor() {
    const now =
      Date.now();

    const millisecondsUntilNextMinute =
      60_000 -
      (
        now %
        60_000
      );

    this.deadlineTimerSubscription =
      timer(
        millisecondsUntilNextMinute,
        60_000
      ).subscribe(
        () => {
          this.currentTime.set(
            Date.now()
          );
        }
      );

    this.route.paramMap.subscribe(
      (params) => {
        const id =
          params.get(
            'id'
          );

        if (!id) {
          this.router.navigate([
            '/wareneingaenge',
          ]);

          return;
        }

        this.currentId.set(
          id
        );

        this.loadWareneingang(
          id
        );
      }
    );
  }

  ngOnDestroy(): void {
    this.stopLockTimers();

    this.deadlineTimerSubscription
      ?.unsubscribe();

    this.releaseLockSilently();

    if (
      this.abschlussNichtMoeglichToastTimeoutId
    ) {
      clearTimeout(
        this.abschlussNichtMoeglichToastTimeoutId
      );
    }

    if (
      this.artikelAddFeedbackTimeoutId
    ) {
      clearTimeout(
        this.artikelAddFeedbackTimeoutId
      );
    }

    if (
      this.bearbeitungsmodusToastTimeoutId
    ) {
      clearTimeout(
        this.bearbeitungsmodusToastTimeoutId
      );
    }
  }

  @HostListener(
    'document:keydown.escape'
  )
  protected handleEscape(): void {
    if (
      this.isAddArtikelDialogOpen()
    ) {
      this.closeAddArtikelDialog();
      return;
    }

    if (
      this.isEditMode()
    ) {
      this.cancelEdit();
    }
  }

  protected goBack(): void {
    this.router.navigate([
      '/wareneingaenge',
    ]);
  }

  protected getStatusLabel(
    status:
      WareneingangStatus
  ): string {
    switch (status) {
      case WareneingangStatus.Offen:
        return 'Offen';

      case WareneingangStatus.InBearbeitung:
        return 'In Bearbeitung';

      case WareneingangStatus.Abgeschlossen:
        return 'Abgeschlossen';
    }
  }

  protected getStatusClass(
    status:
      WareneingangStatus
  ): string {
    switch (status) {
      case WareneingangStatus.Offen:
        return 'status-offen';

      case WareneingangStatus.InBearbeitung:
        return 'status-in-bearbeitung';

      case WareneingangStatus.Abgeschlossen:
        return 'status-abgeschlossen';
    }
  }

  protected formatDisplayDate(
    isoDate: string
  ): string {
    const [
      year,
      month,
      day,
    ] =
      isoDate
        .split('-')
        .map(Number);

    if (
      !year ||
      !month ||
      !day
    ) {
      return isoDate;
    }

    const date =
      new Date(
        year,
        month - 1,
        day
      );

    const weekday =
      new Intl.DateTimeFormat(
        'de-DE',
        {
          weekday:
            'long',
        }
      ).format(
        date
      );

    const formattedDate =
      new Intl.DateTimeFormat(
        'de-DE',
        {
          day:
            '2-digit',

          month:
            '2-digit',

          year:
            'numeric',
        }
      ).format(
        date
      );

    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formattedDate}`;
  }

  protected getLockLabel(
    item:
      WareneingangDetail
  ): string {
    if (
      item.istGesperrt &&
      item.gesperrtVon ===
      this.currentUserName
    ) {
      return 'Von dir gesperrt';
    }

    if (
      item.istGesperrt &&
      item.gesperrtVon
    ) {
      return `Gesperrt von ${item.gesperrtVon}`;
    }

    return 'Frei';
  }

  protected getOffeneMenge(
    position:
      WareneingangPosition
  ): number {
    return position.offeneMenge;
  }

  protected getIstMengeDisplayValue(
    position:
      WareneingangPosition
  ): number {
    return (
      position.istMenge ??
      position.liefermenge
    );
  }

  protected getIstMengeDifference(
    position:
      WareneingangPosition
  ): number {
    return (
      this.getIstMengeDisplayValue(
        position
      ) -
      position.liefermenge
    );
  }

  protected formatIstMengeDifference(
    position:
      WareneingangPosition
  ): string {
    const difference =
      this.getIstMengeDifference(
        position
      );

    if (
      difference > 0
    ) {
      return `+${difference}`;
    }

    return difference.toString();
  }

  protected setDetailSort(
    column:
      DetailSortColumn
  ): void {
    this.detailSortState.update(
      (
        currentSort
      ) => {
        if (
          currentSort.column ===
          column
        ) {
          return {
            column,

            direction:
              currentSort.direction ===
                'asc'
                ? 'desc'
                : 'asc',
          };
        }

        return {
          column,
          direction:
            'asc',
        };
      }
    );
  }

  protected isDetailSortActive(
    column:
      DetailSortColumn
  ): boolean {
    return (
      this.detailSortState()
        .column ===
      column
    );
  }

  protected getDetailSortIcon(
    column:
      DetailSortColumn
  ): string {
    if (
      !this.isDetailSortActive(
        column
      )
    ) {
      return 'expand_more';
    }

    return (
      this.detailSortState()
        .direction ===
        'asc'
        ? 'expand_less'
        : 'expand_more'
    );
  }

  protected getAbweichung(
    position:
      WareneingangPosition
  ): number {
    if (
      position.istMenge ===
      null
    ) {
      return 0;
    }

    return (
      position.istMenge -
      position.liefermenge
    );
  }

  protected getAbweichungLabel(
    position:
      WareneingangPosition
  ): string {
    const abweichung =
      this.getAbweichung(
        position
      );

    if (!abweichung) {
      return '0';
    }

    return abweichung > 0
      ? `+${abweichung}`
      : `${abweichung}`;
  }

  protected startEdit(): void {
    if (
      !this.canStartEdit()
    ) {
      return;
    }

    const currentId =
      this.currentId();

    if (!currentId) {
      return;
    }

    this.wareneingaengeService
      .acquireLock(
        currentId,
        this.currentUserName
      )
      .subscribe(
        (result) => {
          if (
            !result.success
          ) {
            this.toastService.warning(
              'Bearbeitung nicht möglich',

              result.lockedBy
                ? `Der Wareneingang wird aktuell von ${result.lockedBy} bearbeitet.`
                : 'Der Wareneingang konnte nicht gesperrt werden.'
            );

            return;
          }

          if (
            !result.detail
          ) {
            return;
          }

          this.applyWareneingang(
            result.detail
          );

          this.isEditMode.set(
            true
          );

          this.scheduleCurrentLockExpiry(
            currentId
          );

          this.showBearbeitungsmodusToast();
        }
      );
  }

  protected cancelEdit(): void {
    this.stopLockTimers();

    this.isAddArtikelDialogOpen.set(
      false
    );

    this.artikelSucheTerm.set(
      ''
    );

    const currentId =
      this.currentId();

    if (
      this.isPosSession &&
      currentId
    ) {
      this.isEditMode.set(
        false
      );

      this.releasePosLockAndReturnToOverview(
        currentId
      );

      return;
    }

    this.releaseLockSilently();

    this.isEditMode.set(
      false
    );
  }

  protected save(): void {
    const currentId =
      this.currentId();

    if (!currentId) {
      return;
    }

    this.wareneingaengeService
      .saveWareneingang(
        currentId,

        this.draftPositionen(),

        this.currentUserName
      )
      .subscribe(
        (detail) => {
          if (!detail) {
            this.toastService.error(
              'Speichern fehlgeschlagen'
            );

            return;
          }

          this.applyWareneingang(
            detail
          );

          this.stopLockTimers();

          this.isEditMode.set(
            false
          );

          this.isAddArtikelDialogOpen.set(
            false
          );

          this.artikelSucheTerm.set(
            ''
          );

          this.toastService.success(
            'Wareneingang wurde gespeichert'
          );

          if (
            this.isPosSession
          ) {
            this.router.navigate([
              '/wareneingaenge',
            ]);
          }
        }
      );
  }

  protected complete(): void {
    const currentId =
      this.currentId();

    if (!currentId) {
      return;
    }

    if (
      this.draftPositionen().some(
        (position) =>
          position.istMenge ===
          null
      )
    ) {
      this.showAbschlussNichtMoeglichToast();

      return;
    }

    this.wareneingaengeService
      .completeWareneingang(
        currentId,

        this.draftPositionen(),

        this.currentUserName
      )
      .subscribe(
        (detail) => {
          if (!detail) {
            this.toastService.error(
              'Wareneingang konnte nicht abgeschlossen werden'
            );

            return;
          }

          this.applyWareneingang(
            detail
          );

          this.stopLockTimers();

          this.isEditMode.set(
            false
          );

          this.isAddArtikelDialogOpen.set(
            false
          );

          this.artikelSucheTerm.set(
            ''
          );

          this.toastService.success(
            'Wareneingang abgeschlossen',

            `${detail.lieferscheinnummer} wurde als abgeschlossen markiert.`
          );

          if (
            this.isPosSession
          ) {
            this.router.navigate([
              '/wareneingaenge',
            ]);
          }
        }
      );
  }

  protected decreaseIstMenge(
    position:
      WareneingangPosition
  ): void {
    const currentValue =
      position.istMenge ??
      position.liefermenge;

    this.patchDraftPosition(
      position.artikelnummer,
      {
        istMenge:
          Math.max(
            0,
            currentValue - 1
          ),
      }
    );
  }

  protected increaseIstMenge(
    position:
      WareneingangPosition
  ): void {
    const currentValue =
      position.istMenge ??
      position.liefermenge;

    this.patchDraftPosition(
      position.artikelnummer,
      {
        istMenge:
          currentValue +
          1,
      }
    );
  }

  protected updateIstMengeFromInput(
    artikelnummer: string,
    event: Event
  ): void {
    const input =
      event.target as
      HTMLInputElement;

    const rawValue =
      input.value.trim();

    if (!rawValue) {
      this.patchDraftPosition(
        artikelnummer,

        {
          istMenge:
            null,
        }
      );

      return;
    }

    const parsedValue =
      Number(
        rawValue
      );

    if (
      Number.isNaN(
        parsedValue
      )
    ) {
      return;
    }

    this.patchDraftPosition(
      artikelnummer,

      {
        istMenge:
          Math.max(
            0,
            parsedValue
          ),
      }
    );
  }

  protected openAddArtikelDialog(): void {
    const currentId =
      this.currentId();

    if (!currentId) {
      return;
    }

    this.artikelSucheTerm.set(
      ''
    );

    this.wareneingaengeService
      .getVerfuegbareArtikel(
        currentId
      )
      .subscribe({
        next:
          (
            artikel
          ) => {
            this.verfuegbareArtikel.set(
              artikel
            );

            this.isAddArtikelDialogOpen.set(
              true
            );
          },

        error:
          () => {
            this.toastService.error(
              'Artikel konnten nicht geladen werden'
            );
          },
      });
  }

  protected closeAddArtikelDialog(): void {
    this.isAddArtikelDialogOpen.set(
      false
    );

    this.artikelSucheTerm.set(
      ''
    );

    this.clearArtikelAddFeedback();
  }

  protected addArtikel(
    artikel:
      ArtikelkatalogEintrag
  ): void {
    const currentId =
      this.currentId();

    if (!currentId) {
      return;
    }

    this.wareneingaengeService
      .addArtikel(
        currentId,

        Number(
          artikel.artikelnummer
        )
      )
      .subscribe({
        next:
          (
            detail
          ) => {
            if (!detail) {
              return;
            }

            this.applyWareneingang(
              detail
            );

            this.refreshLockAfterChange(
              currentId
            );

            this.verfuegbareArtikel.update(
              (items) =>
                items.filter(
                  (item) =>
                    item.artikelnummer !==
                    artikel.artikelnummer
                )
            );

            this.showArtikelAddFeedback(
              artikel.artikelname
            );
          },

        error:
          () => {
            this.toastService.error(
              'Artikel konnte nicht hinzugefügt werden'
            );
          },
      });
  }

  protected updateArtikelSucheTerm(
    value: string
  ): void {
    this.artikelSucheTerm.set(
      value
    );
  }

  private getDeadlineTimestamp(
    wareneingang:
      WareneingangDetail
  ): number | null {
    const dateMatch =
      wareneingang
        .wareneingangTag
        .match(
          /^(\d{4})-(\d{2})-(\d{2})/
        );

    const timeMatch =
      wareneingang
        .bearbeitungsZeitBis
        .match(
          /^(\d{1,2}):(\d{2})/
        );

    if (
      !dateMatch ||
      !timeMatch
    ) {
      return null;
    }

    const [
      ,
      year,
      month,
      day,
    ] =
      dateMatch;

    const [
      ,
      hour,
      minute,
    ] =
      timeMatch;

    return new Date(
      Number(
        year
      ),

      Number(
        month
      ) - 1,

      Number(
        day
      ),

      Number(
        hour
      ),

      Number(
        minute
      ),

      0,
      0
    ).getTime();
  }

  private sortWarengruppen(
    groups:
      WarengruppeBlock[]
  ): WarengruppeBlock[] {
    const currentSort =
      this.detailSortState();

    if (
      currentSort.column !==
      'artikel'
    ) {
      return groups;
    }

    const directionFactor =
      currentSort.direction ===
        'asc'
        ? 1
        : -1;

    return [
      ...groups,
    ].sort(
      (
        first,
        second
      ) => {
        return (
          first.warengruppe.localeCompare(
            second.warengruppe,
            'de',
            {
              sensitivity:
                'base',
            }
          ) *
          directionFactor
        );
      }
    );
  }

  private loadWareneingang(
    id: string
  ): void {
    this.isLoading.set(
      true
    );

    this.wareneingaengeService
      .getWareneingangById(
        id
      )
      .pipe(
        finalize(() =>
          this.isLoading.set(
            false
          )
        )
      )
      .subscribe({
        next:
          (
            detail
          ) => {
            if (!detail) {
              if (
                this.isPosSession
              ) {
                this.releasePosLockAndReturnToOverview(
                  id
                );
              } else {
                this.router.navigate([
                  '/wareneingaenge',
                ]);
              }

              return;
            }

            if (
              this.isPosSession
            ) {
              /*
               * The POS route guard already acquired the lock
               * before this component was created.
               */
              this.applyWareneingang({
                ...detail,

                istGesperrt:
                  true,

                gesperrtVon:
                  this.currentUserName,
              });

              this.activatePosEditMode(
                id
              );
            } else {
              /*
               * Normal Web Office behaviour:
               * open the detail read-only.
               */
              this.applyWareneingang(
                detail
              );

              this.isEditMode.set(
                false
              );
            }

            this.isAddArtikelDialogOpen.set(
              false
            );

            this.artikelSucheTerm.set(
              ''
            );
          },

        error:
          () => {
            /*
             * If the detail cannot be loaded after the POS guard
             * already acquired a lock, release that lock again.
             */
            if (
              this.isPosSession
            ) {
              this.releasePosLockAndReturnToOverview(
                id
              );
            } else {
              this.router.navigate([
                '/wareneingaenge',
              ]);
            }
          },
      });
  }

  private applyWareneingang(
    detail:
      WareneingangDetail
  ): void {
    this.wareneingang.set(
      detail
    );

    this.draftPositionen.set(
      this.clonePositionen(
        detail.positionen
      )
    );
  }

  private releasePosLockAndReturnToOverview(
    id: string
  ): void {
    this.wareneingaengeService
      .releaseLock(
        id,
        this.currentUserName
      )
      .subscribe({
        next:
          () =>
            this.router.navigate([
              '/wareneingaenge',
            ]),

        error:
          () =>
            this.router.navigate([
              '/wareneingaenge',
            ]),
      });
  }

  private activatePosEditMode(
    id: string
  ): void {
    if (
      !this.isPosSession ||
      this.isEditMode()
    ) {
      return;
    }

    /*
     * A closed or overdue Wareneingang must still
     * respect the existing business rules.
     */
    if (
      !this.canStartEdit()
    ) {
      this.releaseLockSilently();
      return;
    }

    /*
     * POS behaviour:
     * entering the detail means editing immediately.
     * The lock was already acquired by the route guard.
     */
    this.isEditMode.set(
      true
    );

    this.scheduleCurrentLockExpiry(
      id
    );

    this.showBearbeitungsmodusToast();
  }

  private patchDraftPosition(
    artikelnummer: string,

    patch:
      Partial<
        WareneingangPosition
      >
  ): void {
    if (
      !this.isEditMode()
    ) {
      return;
    }

    const currentPosition =
      this.draftPositionen()
        .find(
          (position) =>
            position.artikelnummer ===
            artikelnummer
        );

    if (
      !currentPosition
    ) {
      return;
    }

    if (
      patch.istMenge !==
      undefined &&
      currentPosition.istMenge ===
      patch.istMenge
    ) {
      return;
    }

    this.draftPositionen.update(
      (positionen) =>
        positionen.map(
          (position) =>
            position.artikelnummer ===
              artikelnummer
              ? {
                ...position,
                ...patch,
              }
              : position
        )
    );

    this.scheduleHeartbeatAfterChange();
  }

  private releaseLockSilently(): void {
    const currentId =
      this.currentId();

    const currentWareneingang =
      this.wareneingang();

    if (
      !currentId ||
      !currentWareneingang
        ?.istGesperrt
    ) {
      return;
    }

    if (
      currentWareneingang
        .gesperrtVon !==
      this.currentUserName
    ) {
      return;
    }

    this.wareneingaengeService
      .releaseLock(
        currentId,
        this.currentUserName
      )
      .subscribe(
        (detail) => {
          if (detail) {
            this.applyWareneingang(
              detail
            );
          }
        }
      );
  }

  private clonePositionen(
    positionen:
      WareneingangPosition[]
  ): WareneingangPosition[] {
    return JSON.parse(
      JSON.stringify(
        positionen
      )
    ) as
      WareneingangPosition[];
  }

  private showBearbeitungsmodusToast(): void {
    if (
      this.isBearbeitungsmodusToastVisible
    ) {
      return;
    }

    this.isBearbeitungsmodusToastVisible =
      true;

    this.toastService.info(
      'Bearbeitungsmodus aktiv'
    );

    this.bearbeitungsmodusToastTimeoutId =
      setTimeout(
        () => {
          this.isBearbeitungsmodusToastVisible =
            false;

          this.bearbeitungsmodusToastTimeoutId =
            null;
        },

        4000
      );
  }

  private showAbschlussNichtMoeglichToast(): void {
    if (
      this.isAbschlussNichtMoeglichToastVisible
    ) {
      return;
    }

    this.isAbschlussNichtMoeglichToastVisible =
      true;

    this.toastService.warning(
      'Abschluss nicht möglich',

      'Alle Positionen müssen eine gültige Ist-Menge besitzen.'
    );

    this.abschlussNichtMoeglichToastTimeoutId =
      setTimeout(
        () => {
          this.isAbschlussNichtMoeglichToastVisible =
            false;

          this.abschlussNichtMoeglichToastTimeoutId =
            null;
        },

        4000
      );
  }

  private showArtikelAddFeedback(
    artikelname: string
  ): void {
    if (
      this.artikelAddFeedbackTimeoutId
    ) {
      clearTimeout(
        this.artikelAddFeedbackTimeoutId
      );
    }

    this.artikelAddFeedback.set({
      artikelname,
    });

    this.artikelAddFeedbackTimeoutId =
      setTimeout(
        () => {
          this.artikelAddFeedback.set(
            null
          );

          this.artikelAddFeedbackTimeoutId =
            null;
        },

        3500
      );
  }

  protected clearArtikelAddFeedback(): void {
    if (
      this.artikelAddFeedbackTimeoutId
    ) {
      clearTimeout(
        this.artikelAddFeedbackTimeoutId
      );

      this.artikelAddFeedbackTimeoutId =
        null;
    }

    this.artikelAddFeedback.set(
      null
    );
  }

  private scheduleCurrentLockExpiry(
    id: string
  ): void {
    const lockSession =
      this.wareneingaengeService
        .getLockSession(
          id
        );

    if (
      !lockSession
    ) {
      this.handleLostLock(
        id,

        'Die Sperre ist nicht mehr verfügbar.'
      );

      return;
    }

    this.scheduleLockExpiry(
      lockSession.expiresAt
    );
  }

  private scheduleHeartbeatAfterChange(): void {
    const id =
      this.currentId();

    if (
      !id ||
      !this.isEditMode()
    ) {
      return;
    }

    if (
      this.changeHeartbeatTimerId !==
      null
    ) {
      window.clearTimeout(
        this.changeHeartbeatTimerId
      );
    }

    this.changeHeartbeatTimerId =
      window.setTimeout(
        () => {
          this.changeHeartbeatTimerId =
            null;

          this.refreshLockAfterChange(
            id
          );
        },

        this.CHANGE_HEARTBEAT_DEBOUNCE_MS
      );
  }

  private refreshLockAfterChange(
    id: string
  ): void {
    this.stopLockExpiryTimer();

    this.wareneingaengeService
      .heartbeatLock(
        id
      )
      .subscribe({
        next:
          (
            lockSession
          ) => {
            if (
              !lockSession
            ) {
              this.handleLostLock(
                id,

                'Die Sperre ist nicht mehr verfügbar.'
              );

              return;
            }

            this.scheduleLockExpiry(
              lockSession.expiresAt
            );
          },

        error:
          (
            error:
              unknown
          ) => {
            this.handleLostLock(
              id,

              this.getErrorMessage(
                error,

                'Die Sperre ist abgelaufen oder wurde auf einem anderen Gerät übernommen.'
              )
            );
          },
      });
  }

  private scheduleLockExpiry(
    expiresAt: string
  ): void {
    this.stopLockExpiryTimer();

    const expiresAtMs =
      Date.parse(
        expiresAt
      );

    if (
      Number.isNaN(
        expiresAtMs
      )
    ) {
      return;
    }

    /*
     * Kurz vor dem serverseitigen Redis-Ablauf speichern.
     * Nach expiresAt würde das Backend den Save wegen fehlendem Lock ablehnen.
     */
    const delay =
      Math.max(
        0,

        expiresAtMs -
        Date.now() -
        this.LOCK_EXPIRY_SAFETY_MS
      );

    this.lockExpiryTimerId =
      window.setTimeout(
        () =>
          this.handleLockExpiry(),

        delay
      );
  }

  private handleLockExpiry(): void {
    const id =
      this.currentId();

    if (
      !id ||
      !this.isEditMode()
    ) {
      return;
    }

    this.stopLockTimers();

    this.wareneingaengeService
      .saveWareneingang(
        id,

        this.draftPositionen(),

        this.currentUserName
      )
      .subscribe({
        next:
          (
            detail
          ) => {
            if (detail) {
              this.applyWareneingang(
                detail
              );
            }

            this.isEditMode.set(
              false
            );

            this.isAddArtikelDialogOpen.set(
              false
            );

            this.artikelSucheTerm.set(
              ''
            );

            this.toastService.warning(
              'Bearbeitung beendet',

              'Die Bearbeitungszeit ist abgelaufen. Die Änderungen wurden gespeichert und der Wareneingang wurde wieder freigegeben.'
            );

            if (
              this.isPosSession
            ) {
              this.router.navigate([
                '/wareneingaenge',
              ]);
            }
          },

        error:
          (
            error:
              unknown
          ) => {
            this.releaseLockAfterExpiryError(
              id,

              this.getErrorMessage(
                error,

                'Die Bearbeitungszeit ist abgelaufen. Der Wareneingang konnte nicht mehr gespeichert werden.'
              )
            );
          },
      });
  }

  private releaseLockAfterExpiryError(
    id: string,
    message: string
  ): void {
    this.wareneingaengeService
      .releaseLock(
        id,
        this.currentUserName
      )
      .subscribe({
        next:
          (
            detail
          ) => {
            if (detail) {
              this.applyWareneingang(
                detail
              );
            }

            this.finishExpiredEdit(
              message
            );
          },

        error:
          () =>
            this.finishExpiredEdit(
              message
            ),
      });
  }

  private finishExpiredEdit(
    message: string
  ): void {
    this.isEditMode.set(
      false
    );

    this.isAddArtikelDialogOpen.set(
      false
    );

    this.artikelSucheTerm.set(
      ''
    );

    this.toastService.warning(
      'Bearbeitung beendet',
      message
    );

    if (
      this.isPosSession
    ) {
      this.router.navigate([
        '/wareneingaenge',
      ]);

      return;
    }

    const id =
      this.currentId();

    if (id) {
      this.loadWareneingang(
        id
      );
    }
  }

  private handleLostLock(
    id: string,
    message: string
  ): void {
    this.stopLockTimers();

    this.wareneingaengeService
      .clearLock(
        id
      );

    this.isEditMode.set(
      false
    );

    this.toastService.warning(
      'Sperre nicht mehr aktiv',
      message
    );

    if (
      this.isPosSession
    ) {
      this.router.navigate([
        '/wareneingaenge',
      ]);

      return;
    }

    this.loadWareneingang(
      id
    );
  }

  private stopLockExpiryTimer(): void {
    if (
      this.lockExpiryTimerId ===
      null
    ) {
      return;
    }

    window.clearTimeout(
      this.lockExpiryTimerId
    );

    this.lockExpiryTimerId =
      null;
  }

  private stopLockTimers(): void {
    this.stopLockExpiryTimer();

    if (
      this.changeHeartbeatTimerId !==
      null
    ) {
      window.clearTimeout(
        this.changeHeartbeatTimerId
      );

      this.changeHeartbeatTimerId =
        null;
    }
  }

  private getErrorMessage(
    error: unknown,
    fallback: string
  ): string {
    if (
      typeof error ===
      'string'
    ) {
      return error;
    }

    if (
      !error ||
      typeof error !==
      'object'
    ) {
      return fallback;
    }

    const record =
      error as Record<
        string,
        unknown
      >;

    const errorBody =
      record['error'];

    if (
      typeof errorBody ===
      'string'
    ) {
      return errorBody;
    }

    if (
      errorBody &&
      typeof errorBody ===
      'object'
    ) {
      const body =
        errorBody as Record<
          string,
          unknown
        >;

      const detail =
        body['detail'];

      if (
        typeof detail ===
        'string' &&
        detail.trim()
      ) {
        return detail;
      }

      const message =
        body['message'];

      if (
        typeof message ===
        'string' &&
        message.trim()
      ) {
        return message;
      }
    }

    const message =
      record['message'];

    if (
      typeof message ===
      'string' &&
      message.trim()
    ) {
      return message;
    }

    return fallback;
  }
}